import { ChangeDetectorRef, Component, Input, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { CustomFormValidators } from "@app/directives/_forms/form-validation.service";
import { Place } from "@app/directives/_forms/v-autocomplete/v-places-autocomplete/place";
import { VMapComponent } from "@app/directives/_map/v-map/v-map/v-map.component";
import { IGoogleMapsMarker } from "@app/interfaces/IGoogleMapsMarkerOptions";
import { AccountService } from "@app/services/account.service";
import { ContextualSpeedingOverrideService } from "@app/services/contextual-speeding-override.service";
import { DistanceMeasurement } from "@app/services/preferences.service";
import { UnitConversionService, UnitConversionType } from "@app/services/unitConversion.service";
import { IPolyline } from "@typings/google.maps.angular";
import { ContextualSpeedingEditor } from "../base/contextual-speeding-editor";
import { ContextualSpeedingOverride, ContextualSpeedingOverrideSegment } from "../models/contextual-speeding-override";
import { HereRouteData } from "../models/here-route-data";
import './contextual-speeding-road-editor.component.scss';

const MAX_SPEED_MILES = 100;
const MAX_SPEED_KM = 160;
const MAX_ROUTE_LENGTH_MILES = 10;
const MAX_ROUTE_LENGTH_KM = 16;

@Component({
    selector: 'contextual-speeding-road-editor',
    template: require('./contextual-speeding-road-editor.component.html')
})
export class ContextualSpeedingRoadEditorComponent
    extends ContextualSpeedingEditor<ContextualSpeedingOverride> 
    implements OnInit {
    private isMeasurementInKm: boolean;

    @ViewChild("vMap", { static: false }) vMap: VMapComponent;
    @Input() items: ContextualSpeedingOverride[] = [];

    form: FormGroup;
    map: google.maps.Map;
    markers: IGoogleMapsMarker<string>[] = [];
    polylines: IPolyline[] = [];
    routeData: HereRouteData;
    speedUnit: string;

    maxPolylineLength: string;
    currentInvalidPolylineLength: string;

    showMaxLengthError = false;

    get start(): IGoogleMapsMarker<string> {
        return this.markers[0] || undefined;
    }

    set start(value: IGoogleMapsMarker<string>) {
        this.markers[0] = value;
        this.markers = [...this.markers];
    }

    get end(): IGoogleMapsMarker<string> {
        return this.markers[1] || undefined;
    }

    set end(value: IGoogleMapsMarker<string>) {
        this.markers[1] = value;
        this.markers = [...this.markers];
    }

    get canSave(): boolean {
        return this.form.valid && this.markers.length === 2 && !this.showMaxLengthError;
    }

    constructor(
        private readonly contextualSpeedingService: ContextualSpeedingOverrideService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly accountService: AccountService,
        private readonly unitConversionService: UnitConversionService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.initiateMeasurementType()
        this.initiateForm();
        this.setSpeedUnit();
        this.optionallySetSelectedValues();
    }

    onPlaceSelected(place: Place): void {
        const latLng = new google.maps.LatLng({
            lat: place.lat,
            lng: place.lng
        });

        this.vMap.fitMap([latLng]);
    }

    onMapInitialised(map: google.maps.Map): void {
        this.map = map;
        this.map.addListener("click", async (event: any) => await this.onMapClicked(event?.latLng));
    }

    async save(): Promise<ContextualSpeedingOverride> {
        const overrideSpeed = this.form.get("overrideSpeed").value;
        const overrideSpeedKph = this.unitConversionService.convertToDatabaseUnit(overrideSpeed, UnitConversionType.Speed);

        const segmentIdentifiers: ContextualSpeedingOverrideSegment[] =  this.routeData.SegmentIdentifiers.map(s => {
            const id = this.selectedItem?.ContextualSpeedingOverrideSegments?.find(x => x.SegmentIdentifier == s.SegmentIdentifier)?.Id

            return { 
                Id: id,
                SegmentIdentifier : s.SegmentIdentifier 
            }
        });

        // PostedSpeedLimit is the same for every segment
        const reportedSpeedKph = this.routeData.SegmentIdentifiers[0].PostedSpeedLimit;

        const override: ContextualSpeedingOverride = {
            Id: this.selectedItem?.Id || 0,
            Name: this.form.get('name').value,
            StartLatitude: this.start.options.position.lat,
            StartLongitude: this.start.options.position.lng,
            EndLatitude: this.end.options.position.lat,
            EndLongitude: this.end.options.position.lng,
            EncodedPolyline: this.routeData.EncodedPolyline,
            OverrideSpeedKph: overrideSpeedKph,
            ReportedSpeedKph: reportedSpeedKph,
            AccountId: this.accountService.getAccountDataFromCache().Id,
            ContextualSpeedingOverrideSegments: segmentIdentifiers
        }

        return !!this.selectedItem
            ? await this.contextualSpeedingService.updateContextualSpeedingOverride(override)
            : await this.contextualSpeedingService.addContextualSpeedingOverride(override);
    }

    clearMarkers(): void {
        this.markers = [];
        this.polylines = [];
        this.routeData = undefined;
        this.showMaxLengthError = false;

        this.changeDetectorRef.detectChanges();
    }

    private initiateForm(): void {
        const maxSpeed = this.isMeasurementInKm ? MAX_SPEED_KM : MAX_SPEED_MILES;    
        const validationItems = this.items.filter(x => x.Id !== this.selectedItem?.Id);
        const validationObjectKey = 'Name' as keyof ContextualSpeedingOverride;

        this.form = new FormGroup({
            name: new FormControl('', [
                Validators.required, 
                Validators.maxLength(80), 
                CustomFormValidators.duplicateProperty(validationItems, validationObjectKey, 'roadSegmentNameDuplicated')
            ]),
            overrideSpeed: new FormControl('', [Validators.required, Validators.min(1), Validators.max(maxSpeed)])
        });
    }

    private initiateMeasurementType(): void {
        const accountOption = this.unitConversionService.getAccountOptions();

        this.isMeasurementInKm = accountOption.DistanceMeasurement.Value === DistanceMeasurement.Kilometres;

        this.maxPolylineLength = this.isMeasurementInKm
            ? `${MAX_ROUTE_LENGTH_KM} km`
            : `${MAX_ROUTE_LENGTH_MILES} mi.`;
    }

    private async onMapClicked(latLngs: google.maps.LatLng): Promise<void> {
        if (!latLngs || (!!this.start && !!this.end)) {
            return;
        }

        const lat = latLngs.lat();
        const lng = latLngs.lng();

        if (!lat || !lng) {
            return;
        }

        await this.addMarker({ lat, lng });
    }

    private async addMarker(latLngs: google.maps.LatLngLiteral): Promise<void> {
        const markerType = !this.start ? 'start' : 'end';
        const marker = this.contextualSpeedingService.createMapMarker([latLngs.lat, latLngs.lng], markerType);

        if (markerType === 'start') {
            this.start = marker;
        } else if (markerType === 'end') {
            this.end = marker;
        }

        await this.checkForPolylineUpdate();
        this.changeDetectorRef.detectChanges();
    }

    private async checkForPolylineUpdate(): Promise<void> {
        if (!this.start || !this.end) {
            return;
        }

        const points: [number, number][] = [
            [this.start.options.position.lat, this.start.options.position.lng],
            [this.end.options.position.lat, this.end.options.position.lng],
        ]
        
        this.routeData = await this.contextualSpeedingService.getContextualSpeedingOverrideRoute(points);

        const polylineValidationResult = this.checkPolylineLength(this.routeData.DistanceKm);

        if (!polylineValidationResult.valid) {
            this.showMaxLengthError = true;
            this.currentInvalidPolylineLength = polylineValidationResult.distance;
        }

        const mapPoints = this.routeData.Points.map(x => new google.maps.LatLng(x[0], x[1]));
        const polyline = this.contextualSpeedingService.createPolyline(mapPoints);

        this.polylines = [polyline];

        this.vMap.fitMap(mapPoints);
        await this.adjustMarkersToPolyline(mapPoints);
        
        this.changeDetectorRef.detectChanges();
    }

    private checkPolylineLength(distanceKm: number): { valid: boolean, distance: string } {
        if (this.isMeasurementInKm) {
            return {
                valid: distanceKm <= MAX_ROUTE_LENGTH_KM,
                distance: `${Math.floor(distanceKm)} km`
            };
        } else {
            const distanceMiles = this.unitConversionService.convertKilometersToMiles(distanceKm);

            return {
                valid: distanceMiles <= MAX_ROUTE_LENGTH_MILES,
                distance: `${Math.floor(distanceMiles)} mi.`
            }
        }
    }

    private async optionallySetSelectedValues(): Promise<void> {
        if (!this.selectedItem) {
            return;
        }

        const overrideSpeed = this.unitConversionService
            .convertToUserUnit(this.selectedItem.OverrideSpeedKph, UnitConversionType.Speed);

        this.form.patchValue({
            name: this.selectedItem.Name,
            overrideSpeed: overrideSpeed
        });

        await this.setMapValuesFromSelectedItem();
    }

    private async setMapValuesFromSelectedItem(): Promise<void> {
        const points = await this.contextualSpeedingService.getPointsForContextualSpeedingOverride(this.selectedItem.Id);

        const start = points[0];
        const end = points[points.length - 1];

        this.start = this.contextualSpeedingService.createMapMarker([start[0], start[1]], 'start');
        this.end = this.contextualSpeedingService.createMapMarker([end[0], end[1]], 'end');

        await this.checkForPolylineUpdate();
    }

    private async adjustMarkersToPolyline(mapPoints: google.maps.LatLng[]): Promise<void> {
        const first = mapPoints[0];
        const last = mapPoints[mapPoints.length - 1];

        this.start = this.contextualSpeedingService.createMapMarker([first.lat(), first.lng()], 'start')
        this.end = this.contextualSpeedingService.createMapMarker([last.lat(), last.lng()], 'end');

        this.fulfillMarkersAddresses();
    }

    private fulfillMarkersAddresses(): void {
        this.contextualSpeedingService.getAddressForMarker(this.start).then(address => {
            // It causes proper HTML update. Detector fails, setTimeout fails...
            this.start = { ...this.start, model: address };
        });

        this.contextualSpeedingService.getAddressForMarker(this.end).then(address => {
            // It causes proper HTML update. Detector fails, setTimeout fails...
            this.end = { ...this.end, model: address };
        });
    }

    private setSpeedUnit(): void {
        this.speedUnit = this.unitConversionService.getConversionLabel(UnitConversionType.Speed);
    }
}
